import { memo } from 'react';

import { useUserTransactionsQuery } from '../../../../hooks/useDashboardQueries';
import { TransactionV2 } from '../../../../types/dashboardV2';
import {
  formatTransactionStatus,
  getMobileTransactionStatusBadgeClass,
  getMobileTransactionStatusDotClass,
} from '../../../../shared/utils/statusUtils';
import { formatDateTime } from '../../../../shared/utils/dateFormat';
import { formatUAHGrouped } from '../../../../shared/utils/currency';
import { HistoryColumn, HistoryPanel } from './HistoryPanel';
import type { PaginationState } from '../../../../hooks/usePaginationState';

const TransactionStatusBadge = ({ transaction }: { transaction: TransactionV2 }) => (
  <span className={`mobile-status-badge ${getMobileTransactionStatusBadgeClass(transaction.status)}`}>
    <span className={`mobile-status-dot ${getMobileTransactionStatusDotClass(transaction.status)}`} />
    {formatTransactionStatus(transaction.status)}
  </span>
);

const COLUMNS: HistoryColumn<TransactionV2>[] = [
  {
    headerKey: 'dashboard.userDetails.tableTransaction',
    width: '22%',
    renderCell: (tx) => tx.tranId || '—',
  },
  {
    headerKey: 'dashboard.userDetails.tableInvoice',
    width: '18%',
    renderCell: (tx) => tx.invoiceId || '—',
  },
  {
    headerKey: 'dashboard.userDetails.tableBank',
    width: '18%',
    renderCell: (tx) => tx.bank || '—',
  },
  {
    headerKey: 'dashboard.userDetails.tableAmount',
    width: '14%',
    numeric: true,
    renderCell: (tx) => formatUAHGrouped(tx.amount),
  },
  {
    headerKey: 'dashboard.userDetails.tableStatus',
    width: '14%',
    renderCell: (tx) => <TransactionStatusBadge transaction={tx} />,
  },
  {
    headerKey: 'dashboard.userDetails.tableDate',
    width: '14%',
    numeric: true,
    renderCell: (tx) => formatDateTime(tx.createdAt),
  },
];

export const TransactionsPanel = memo(function TransactionsPanel({
  userId,
  pagination,
}: {
  userId: string;
  pagination: PaginationState;
}) {
  const { data, isLoading, error, refetch } = useUserTransactionsQuery({
    userId,
    page: pagination.page,
    pageSize: pagination.rowsPerPage,
  });

  return (
    <HistoryPanel<TransactionV2>
      items={data?.data ?? []}
      total={data?.total ?? 0}
      isLoading={isLoading}
      error={error}
      onRetry={() => refetch()}
      pagination={pagination}
      errorFallbackKey="dashboard.userDetails.errorTransactions"
      loadingKey="dashboard.userDetails.loadingTransactions"
      emptyIcon="receipt_long"
      emptyTitleKey="dashboard.userDetails.noTransactionsYet"
      emptySubtitleKey="dashboard.userDetails.noTransactionsHint"
      columns={COLUMNS}
      getRowKey={(tx) => tx.id}
      renderMobileCard={(tx, t) => (
        <div key={tx.id} className="user-details-card" role="article">
          <div className="user-details-card-row">
            <div className="user-details-card-title">
              <span className="material-symbols-outlined" aria-hidden="true">receipt_long</span>
              {tx.tranId || tx.invoiceId || t('dashboard.userDetails.transactionFallback')}
            </div>
            <TransactionStatusBadge transaction={tx} />
          </div>
          <div className="user-details-card-sub">
            {tx.bank ? `${tx.bank} · ` : ''}
            {formatDateTime(tx.createdAt)}
          </div>
          <div className="user-details-card-divider" />
          <div className="user-details-card-row">
            <span className="user-details-card-label">{t('dashboard.userDetails.tableAmount')}</span>
            <span className="user-details-card-value">{formatUAHGrouped(tx.amount)}</span>
          </div>
          {tx.invoiceId && (
            <div className="user-details-card-row">
              <span className="user-details-card-label">{t('dashboard.userDetails.tableInvoice')}</span>
              <span className="user-details-card-value" style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                {tx.invoiceId}
              </span>
            </div>
          )}
        </div>
      )}
    />
  );
});
